import { useCallback, useState, useEffect, useMemo, useContext } from 'react';
import axios from 'axios';
import errorReporter from '@app/errorReporter';
import type { ContactType, ConversationType, MessagingMe } from 'components';
import { cleanConvo, message } from 'components';
import { AuthContext } from 'contexts';
import { isAssumeOrgType } from 'utils';

export type MessageSearchState = {
  text: string;
  dateFrom?: Date;
  dateTo?: Date;
  user?: ContactType;
};

export type SelectedRep = {
  id: string;
  avatarFilestackId?: string;
  firstName: string;
  lastName: string;
  location: string;
};

type Brokerage = {
  _id: string;
  mls_name: string;
  [i: string]: string;
};

const useUserMessages = (
  { text, dateFrom, dateTo, user }: MessageSearchState,
  selectedUserId?: string,
) => {
  const { auth, setPusherEvents } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<ConversationType[]>([]);
  const [me, setMe] = useState<MessagingMe>();
  const [pageNumber, setPageNumber] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isValidExport, setIsValidExport] = useState(false);
  const [orgNames, setOrgNames] = useState<{ [i: string]: string }>({});

  const socketConvoUpdated = (data: ConversationType) => {
    if (
      isAssumeOrgType(auth.org.type) ||
      !auth.isAssumedNotBB ||
      data.org.toString() === auth.org.id
    ) {
      setMe(curMe => {
        if (data.latestSender.toString() === curMe.id) return curMe;
        return {
          ...curMe,
          unreadConvos: [data].concat(
            curMe.unreadConvos.filter(({ id }) => id !== data.id),
          ),
        };
      });
      setMessages(curConvos =>
        [data].concat(curConvos.filter(({ id }) => id !== data.id)),
      );
    }
  };

  const socketReadAll = () => {
    setMe(curMe => ({ ...curMe, unreadConvos: [] }));
  };

  const socketRead = (convoRead: ConversationType) => {
    setMe(curMe => ({
      ...curMe,
      unreadConvos: curMe.unreadConvos.filter(
        curConvo => curConvo.id !== convoRead?.id,
      ),
    }));
    if (convoRead?.remove) {
      setMessages(curConvos =>
        curConvos.filter(convo => convo.id !== convoRead.id),
      );
    }
  };

  const getConvos = useCallback(
    async (page = 0) => {
      setIsLoading(true);
      if (page === 0) {
        setMessages([]);
        setIsValidExport(false);
      }
      try {
        const params = {
          search: text,
          dateFrom: dateFrom?.toISOString(),
          dateTo: dateTo?.toISOString(),
          page: page.toString(),
          user: selectedUserId || user?.id,
        };
        const [{ data }, validateData] = await Promise.all([
          axios.get<ConversationType[]>('/messaging/v1/convo', { params }),
          page === 0 &&
            axios.get<{ isValid: boolean; count: number | string }>(
              '/messaging/v1/convo/validateCount',
              { params },
            ),
        ]);

        // getting org names from convos
        if (isAssumeOrgType(auth.org.type)) {
          const result = await axios.get<{ brokerages: Brokerage[] }>(
            `/answering/brokerages`,
          );
          const tempOrgs: { [i: string]: string } = {};
          result.data.brokerages.forEach(brokerage => {
            tempOrgs[brokerage.id] = brokerage.mls_name;
          });
          setOrgNames(tempOrgs);
        }

        // do not display unread for conversations from other user
        let unreadHiddenData: ConversationType[];
        if (user && user.id !== me.id) {
          unreadHiddenData = data.map(msg => ({ ...msg, isUnread: false }));
        }

        if (page === 0) {
          setMessages(unreadHiddenData || data);
          const {
            data: { isValid },
          } = validateData;
          setIsValidExport(isValid);
        } else {
          setMessages(prev => [...prev, ...(unreadHiddenData || data)]);
        }
        setHasMore(data.length === 10);
        setPageNumber(page + 1);
      } catch (err) {
        errorReporter.error(err);
        message.error('Error loading messages');
      } finally {
        setIsLoading(false);
      }
    },
    [text, dateFrom, dateTo, user, selectedUserId],
  );

  const getMe = async () => {
    try {
      const { data } = await axios.get<MessagingMe>(`/messaging/me`);
      setMe(data);
    } catch (err) {
      errorReporter.error(err);
    }
  };

  const loadMoreConvos = useCallback(async () => {
    await getConvos(pageNumber);
  }, [getConvos, pageNumber]);

  useEffect(() => {
    getMe();
  }, []);

  useEffect(() => {
    const toggleSockets = (isBind: boolean) => {
      setPusherEvents('userim', 'convoUpdated', socketConvoUpdated, isBind);
      setPusherEvents('userim', 'convoReadAll', socketReadAll, isBind);
      setPusherEvents('userim', 'convoRead', socketRead, isBind);
    };
    if (me?.id && setPusherEvents && !selectedUserId && !user) {
      toggleSockets(true);
      return () => toggleSockets(false);
    }
  }, [me?.id, setPusherEvents, selectedUserId, user]);

  const conversations = useMemo(() => {
    if (!me) return [];
    return messages.map(convo => cleanConvo(convo, me));
  }, [me, messages]);

  return {
    messagingAuth: me,
    conversations,
    orgNames,
    isLoading,
    hasMore,
    isValidExport,
    getConvos,
    loadMoreConvos,
  };
};

export default useUserMessages;
